import { Box, Typography } from "@mui/material";
import { ReferenceArrayField, ReferenceField, SingleFieldList, Tab, useGetOne, useRecordContext } from "react-admin";
import { grants } from "../backoffice.access_control";
import { StudentDrivingLessonsTab } from "./student/StudentDrivingLessonsTab";
import { StudentTheoryTab } from "./student/StudentTheoryTab";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Student } from "../providers/studentsProvider";
import { StudentAdminForm } from "./student/StudentAdminForm";
import { DrivingSchool } from "../providers/drivingSchoolsProvider";
import { StudentOverviewTab } from "./student/StudentOverviewTab";
import { StudentInvoicesList } from "./student/StudentInvoicesList";
import { Tabs } from "../misc/Tabs";
import { ShowBase } from "../misc/ShowBase";
import { Row } from "../misc/Row";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { useEffect } from "react";
import { StudentStickyNotesList } from "./student/StudentStickyNotesList";
import { StudentInAppNotificationsList } from "./student/StudentInAppNotificationsList";
import { StudentBranchField } from "../fields/StudentBranchField";
import { DateField } from "../fields/DateField";
import { BookedQuotesDialog } from "../dialogs/BookedQuotesDialog";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { ProfileCard } from "./student/cards/ProfileCard";
import { NotesAndDocuments } from "../lists/NotesAndDocuments";
import { RightCollapsibleSidebar } from "../misc/RightCollapsibleSidebar";
import { safeLocalStorage } from "../utils/safeLocalStorage";
import { StudentDetails2 } from "./StudentDetails2";
import { StudentAttendanceTab } from "./student/StudentAttendanceTab";
import { ChangeStudentBranchDialog } from "../dialogs/ChangeStudentBranchDialog";
import { useDialog } from "../hooks/useDialog";
import { LabeledAndEditable } from "../misc/LabeledAndEditable";
import { AssignInstructorDialog } from "../dialogs/AssignInstructorDialog";
import EditIcon from "@mui/icons-material/EditOutlined";

export function StudentDetails() {
  const student = useRecordAndSwitchDrivingSchoolIfNecessary<Student>((it) => it.drivingSchoolId);

  useEffect(() => {
    // For backwards compatibility ...
    if (location.pathname.endsWith("/packages")) {
      const url = new URL(location.href);
      url.pathname = url.pathname.substring(0, url.pathname.length - "/packages".length);
      url.searchParams.set("openDialog", BookedQuotesDialog.name);
      location.replace(url.toString());
    }
  }, []);

  if (location.pathname.endsWith("/packages")) {
    return null;
  }

  if (!student) {
    return <LoadingIndicator />;
  }

  if (safeLocalStorage.getItem("previewStudentDetails2") === "on") {
    return <StudentDetails2 />;
  }

  return (
    <div data-testid="StudentDetails">
      {/* The key is needed here to make sure that the component is re-mounted when the student changes! */}
      <StudentDetailsWithTabs key={student.id} student={student} />
    </div>
  );
}

export function StudentDetailsWithTabs(props: { student: Student }) {
  const { student } = props;
  const trainingIncludesTheoryLessonsOrExam =
    student.bookedTrainings.some((training) => training.hasCompulsoryTheoryLessons) ||
    student.bookedTrainings.some((training) => training.includesTheoryExam);

  return (
    <ShowBase resource="students" id={student.id}>
      <Row
        sx={{
          flexWrap: "wrap",
          columnGap: 0,
          rowGap: 3,
          containerType: "inline-size",
          containerName: "student-details-layout",
          "& > .StudentDetails-tabs": {
            marginLeft: "24px",
            marginRight: { xs: "48px", md: "24px" },
          },
          "&:has(> .RightCollapsibleSidebar-drawerModeMarker) > .StudentDetails-tabs": {
            marginRight: 0,
          },
          "@container student-details-layout (max-width: 751px)": {
            "& > .StudentDetails-profileColumn": { display: "contents" },
            "& > .StudentDetails-tabs": { display: "contents" },
            "& > .StudentDetails-tabs > .MuiStack-root": { display: "contents" },
            "& > .StudentDetails-profileColumn > .StudentDetails-nameCard": {
              order: 1,
              flex: "1 1 100%",
              width: "100%",
            },
            "& > .StudentDetails-tabs > .MuiStack-root > :nth-of-type(1)": {
              order: 3,
              flex: "1 1 100%",
              width: "100%",
            },
            "& > .StudentDetails-profileColumn > .StudentDetails-profileDetailsCard": {
              order: 2,
              flex: "1 1 100%",
              width: "100%",
            },
            "& > .StudentDetails-tabs > .MuiStack-root > :nth-of-type(2)": { display: "none" },
            "& > .StudentDetails-tabs > .MuiStack-root > :nth-of-type(3)": {
              order: 4,
              flex: "1 1 100%",
              width: "100%",
              minWidth: 0,
            },
            "& > :last-child": { order: 5 },
          },
        }}
      >
        <ProfileCard trainingIncludesTheoryLessonsOrExam={trainingIncludesTheoryLessonsOrExam} />
        <Tabs
          className="StudentDetails-tabs"
          sx={{
            ...overviewCardStyle,
            padding: "5px 30px 10px 30px",
            "&:has(.NewDesignTabbedShowLayout-select)": { paddingBottom: "16px" },
            "& .NewDesignTabbedShowLayout-select": { marginBottom: 0 },
          }}
          style={{ flex: "2 1 340px", minWidth: 0 }}
          header={<StudentDetailsHeader />}
          mobileSelectAriaLabel="Profilbereich"
          separateCards
          syncWithLocation
        >
          <Tab label="Status" /* no path attribute here, so the tab is shown by default */>
            <StudentOverviewTab
              student={student}
              trainingIncludesTheoryLessonsOrExam={trainingIncludesTheoryLessonsOrExam}
            />
          </Tab>
          <Tab label="Anwesenheit" path="attendance">
            <StudentAttendanceTab />
          </Tab>
          <Tab label="Theorie" path="theoryLessons">
            <StudentTheoryTab />
          </Tab>
          <Tab label="Fahrstunden" path="drivingLessons">
            <StudentDrivingLessonsTab />
          </Tab>
          {grants.has("Invoice:listPerStudent") && (
            <Tab label="Abrechnung" path="invoices">
              <StudentInvoicesList />
            </Tab>
          )}
          {grants.has("admin") && (
            <Tab label="Admin" path="admin">
              <Box sx={{ containerType: "inline-size" }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "15px",
                    "& > *": { minWidth: 0 },
                    "@container (max-width: 900px)": { gridTemplateColumns: "minmax(0, 1fr)" },
                  }}
                >
                  <StudentAdminForm />
                  <Box sx={{ ...overviewCardStyle, minWidth: 0 }}>
                    <Tabs syncWithLocation={false} fullWidth>
                      <Tab label="Sticky Notes">
                        <StudentStickyNotesList />
                      </Tab>
                      <Tab label="In-App-Benachrichtigungen">
                        <StudentInAppNotificationsList />
                      </Tab>
                    </Tabs>
                  </Box>
                </Box>
              </Box>
            </Tab>
          )}
        </Tabs>
        <RightCollapsibleSidebar
          drawerBelowContainerWidth={1068}
          title={student.name}
          expandedSx={{
            flex: "0 0 300px",
            width: "300px",
            maxWidth: "100%",
            "@container student-details-layout (min-width: 1400px)": {
              flex: "0 0 340px",
              width: "340px",
            },
            "@container student-details-layout (max-width: 751px)": {
              flex: "1 1 100%",
              width: "100%",
            },
          }}
        >
          <Box sx={{ borderRadius: "16px", overflow: "hidden" }}>
            <NotesAndDocuments source="student" />
          </Box>
        </RightCollapsibleSidebar>
      </Row>
    </ShowBase>
  );
}

function StudentDetailsHeader() {
  return (
    <Box
      data-testid="student-details-header-fields"
      sx={{
        alignItems: "baseline",
        display: "grid",
        columnGap: "15px",
        rowGap: { xs: "4px", md: "10px" },
        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
        mt: { xs: 1, md: 2.5 },
        mb: "10px",
        "& .StudentDetailsHeader-field": { minWidth: 0 },
      }}
    >
      <_RegistrationField />
      <_BranchField />
      <_InstructorField />
    </Box>
  );
}

function _BranchField() {
  const { dialogProps, openDialog } = useDialog("ChangeStudentBranchDialog");
  const student = useRecordContext<Student>();
  const { data: drivingSchool } = useGetOne<DrivingSchool>("drivingSchools", { id: student.drivingSchoolId });
  if (!drivingSchool || drivingSchool?.branches?.length <= 1) {
    return null;
  }
  return (
    <>
      <Box className="StudentDetailsHeader-field">
        <LabeledAndEditable label="Filiale" onClick={openDialog}>
          <StudentBranchField />
        </LabeledAndEditable>
      </Box>
      <ChangeStudentBranchDialog {...dialogProps} />
    </>
  );
}

function _InstructorField() {
  const { dialogProps, openDialog } = useDialog("AssignInstructorDialog");
  const disableEdit = !grants.has("Student:assignInstructor");
  return (
    <>
      <Box className="StudentDetailsHeader-field" sx={{ overflow: "hidden", "& > *": { minWidth: 0, width: "100%" } }}>
        <LabeledAndEditable
          label="Fahrlehrer"
          // Added for hover effect to work
          onClick={() => {}}
          disableEdit={disableEdit}
          endIcon={<EditIcon fontSize="small" />}
          endIconClick={disableEdit ? undefined : openDialog}
          endIconAriaLabel="Fahrlehrer ändern"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "start",
              gap: "5px",
              lineHeight: 1,
              flex: "1 1 auto",
              minWidth: 0,
              overflow: "hidden",
              "& .RaReferenceField-link, & .RaReferenceField-link > *": {
                display: "block",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          >
            <ReferenceArrayField
              label="Fahrlehrer"
              reference="instructors"
              source="instructorIds"
              sx={{ minWidth: 0, width: "100%", "& > span": { display: "block", minWidth: 0, width: "100%" } }}
            >
              <SingleFieldList
                empty={<Typography variant="body2">Nicht zugewiesen</Typography>}
                linkType={false}
                sx={{ display: "flex", flexDirection: "column", gap: "0", minWidth: 0, width: "100%" }}
              >
                <ReferenceField
                  source="id"
                  reference="instructors"
                  sx={{ display: "block", minWidth: 0, width: "100%" }}
                />
              </SingleFieldList>
            </ReferenceArrayField>
          </Box>
        </LabeledAndEditable>
      </Box>
      {!disableEdit && <AssignInstructorDialog {...dialogProps} />}
    </>
  );
}

function _RegistrationField() {
  return (
    <Box className="StudentDetailsHeader-field">
      <LabeledAndEditable label="Anmeldung" disableEdit>
        <DateField source="startDate" emptyText="Keine Angabe" />
      </LabeledAndEditable>
    </Box>
  );
}
